import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Search,
  UserPlus,
  Mail,
  ShieldAlert,
  Eye,
  Shield,
  Users,
  ShieldCheck,
  Clock,
  Calendar,
  Activity,
  Pencil,
  EyeOff,
  ChevronDown,
} from "lucide-react";
import { Modal } from "@repo/core-ui";
import { Button, Input, Textarea } from "@repo/core-ui";
import {
  CreateRole,
  RoleList,
  UpdateRole,
  adminCreate,
  adminList,
} from "@repo/api-client";
import toast from "react-hot-toast";
import type {
  RoleItem,
  AdminCreateRequest,
  AdminItem,
  AdminListRequest,
} from "@repo/types";

// ─── Lightweight date formatter (replaces moment.js) ────────────────────────
const formatDate = (iso: string) =>
  new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(iso));

const formatDateTime = (iso: string) =>
  new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(iso));
// ─────────────────────────────────────────────────────────────────────────────

const AdminUsers = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [selectedUser, setSelectedUser] = useState<AdminItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateRoleModalOpen, setIsCreateRoleModalOpen] = useState(false);
  const [roleView, setRoleView] = useState<"list" | "add" | "edit">("list");
  const [roles, setRoles] = useState<RoleItem[]>([]);
  const [selectedRole, setSelectedRole] = useState<RoleItem | null>(null);
  const [roleName, setRoleName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [isCreatingRole, setIsCreatingRole] = useState(false);
  const [isFetchingRoles, setIsFetchingRoles] = useState(false);
  const [admins, setAdmins] = useState<AdminItem[]>([]);
  const [isFetchingAdmins, setIsFetchingAdmins] = useState(false);
  const [totalAdmins, setTotalAdmins] = useState(0);

  // Create Admin State
  const [isCreateAdminModalOpen, setIsCreateAdminModalOpen] = useState(false);
  const [isAdminCreating, setIsAdminCreating] = useState(false);
  const [adminForm, setAdminForm] = useState<AdminCreateRequest>({
    Username: "",
    Email: "",
    PasswordHash: "",
    RoleID: 0,
    ActionPinHash: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showPin, setShowPin] = useState(false);

  // ─── O(1) role lookup map instead of O(n) find() per row ────────────────
  const roleMap = useMemo(
    () => new Map(roles.map((r) => [r.RoleID, r.RoleName])),
    [roles],
  );
  // ─────────────────────────────────────────────────────────────────────────

  const fetchAdmins = useCallback(async () => {
    setIsFetchingAdmins(true);
    try {
      const payload: AdminListRequest = {
        AdminID: null,
        Username: null,
        Email: null,
        RoleID: null,
        IsActive: statusFilter === "all" ? null : statusFilter === "active",
        IsLocked: null,
        Search: searchQuery,
        PageNumber: 1,
        PageSize: 15,
        SortBy: "Username",
        SortDir: "ASC",
      };
      const response = await adminList(payload);
      if (response?.Success && Array.isArray(response?.Data)) {
        setAdmins(response.Data);
        setTotalAdmins(
          response.Data.length > 0 ? (response.Data[0]?.TotalCount ?? 0) : 0,
        );
      } else {
        setAdmins([]);
        setTotalAdmins(0);
      }
    } catch (error) {
      console.error("Error fetching admins:", error);
    } finally {
      setIsFetchingAdmins(false);
    }
  }, [searchQuery, statusFilter]);

  // Debounced fetch on search/filter change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchAdmins();
    }, 500);
    return () => clearTimeout(timer);
  }, [fetchAdmins]);

  const handleViewDetails = useCallback((user: AdminItem) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  }, []);

  const fetchRoles = useCallback(async () => {
    setIsFetchingRoles(true);
    try {
      const response = await RoleList({
        PageNumber: 1,
        PageSize: 100,
        Search: "",
        SortBy: "RoleName",
        SortDir: "ASC",
      } as any);

      if (
        response?.Success &&
        Array.isArray(response?.Data) &&
        response.Data.length > 0 &&
        Array.isArray(response.Data[0]?.Items)
      ) {
        setRoles(response.Data[0].Items);
      } else {
        setRoles([]);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      setRoles([]);
      toast.error("Failed to load roles");
    } finally {
      setIsFetchingRoles(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleCreateRole = useCallback(async () => {
    if (!roleName) {
      toast.error("Role name is required");
      return;
    }

    setIsCreatingRole(true);
    try {
      let response;
      if (roleView === "add") {
        response = await CreateRole({
          RoleName: roleName,
          Description: roleDescription,
        });
      } else {
        response = await UpdateRole({
          RoleID: selectedRole?.RoleID,
          RoleName: roleName,
          Description: roleDescription,
        });
      }

      if (response.Success) {
        toast.success(
          roleView === "add"
            ? "Role created successfully"
            : "Role updated successfully",
        );
        fetchRoles();
        setRoleView("list");
        setRoleName("");
        setRoleDescription("");
      } else {
        toast.error(response.Message || "Failed to process role");
      }
    } catch {
      toast.error("An unexpected error occurred");
    } finally {
      setIsCreatingRole(false);
    }
  }, [roleName, roleDescription, roleView, selectedRole, fetchRoles]);

  const handleEditRole = useCallback((role: RoleItem) => {
    setSelectedRole(role);
    setRoleName(role.RoleName);
    setRoleDescription(role.Description || "");
    setRoleView("edit");
  }, []);

  const openRoleManagement = useCallback(() => {
    setRoleView("list");
    setIsCreateRoleModalOpen(true);
    fetchRoles();
  }, [fetchRoles]);

  const handleCreateAdmin = useCallback(async () => {
    if (
      !adminForm.Username ||
      !adminForm.Email ||
      !adminForm.PasswordHash ||
      !adminForm.RoleID ||
      !adminForm.ActionPinHash
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    setIsAdminCreating(true);
    try {
      const response = await adminCreate(adminForm);
      if (response.Success) {
        toast.success("Admin created successfully");
        setIsCreateAdminModalOpen(false);
        setAdminForm({
          Username: "",
          Email: "",
          PasswordHash: "",
          RoleID: 0,
          ActionPinHash: "",
        });
        // Refresh admin list after successful creation
        fetchAdmins();
      } else {
        toast.error(response.Message || "Failed to create admin");
      }
    } catch {
      toast.error("An unexpected error occurred");
    } finally {
      setIsAdminCreating(false);
    }
  }, [adminForm, fetchAdmins]);

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-[#111c44] p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-white/10">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-800 dark:text-slate-100">
            Admin Protocol
          </h1>
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mt-1">
            Secure terminal for access control and authorization
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={openRoleManagement}
            className="h-10 px-5 bg-white dark:bg-[#1b254b] border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-[#1b254b]/80 text-slate-700 dark:text-slate-200 rounded-xl text-sm font-semibold transition-all flex items-center gap-2 shadow-sm"
          >
            <ShieldAlert
              size={18}
              className="text-blue-600 dark:text-blue-400"
            />
            Role Matrix
          </button>
          <button
            onClick={() => setIsCreateAdminModalOpen(true)}
            className="h-10 px-5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold transition-all flex items-center gap-2 shadow-sm"
          >
            <UserPlus size={18} />
            Mint Admin
          </button>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-white dark:bg-[#111c44] p-4 rounded-2xl shadow-sm border border-slate-200 dark:border-white/10 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
            size={18}
          />
          <input
            type="text"
            placeholder="Scan personnel by name or comms..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 h-11 bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-white/10 focus:border-blue-500 dark:focus:border-white/20 outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500 font-medium text-slate-700 dark:text-slate-200"
          />
        </div>
        {/* <div className="flex gap-3">
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as "all" | "active" | "inactive")
            }
            className="h-11 px-4 bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 rounded-xl text-sm font-medium text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-white/10 focus:border-blue-500 dark:focus:border-white/20 outline-none transition-all min-w-[140px]"
          >
            <option value="all">Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div> */}
      </div>

      {/* Stats Badge */}
      {totalAdmins > 0 && (
        <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 px-1">
          Showing {admins.length} of {totalAdmins} administrators
        </p>
      )}

      {/* Main Table */}
      <div className="bg-white dark:bg-[#111c44] rounded-2xl shadow-sm border border-slate-200 dark:border-white/10 overflow-hidden">
        <div className="overflow-x-auto min-h-[400px]">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 dark:bg-[#1b254b]/50 border-b border-slate-200 dark:border-white/10">
                <th className="px-6 py-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Administrator
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Role & Access
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-white/5">
              {isFetchingAdmins ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <div className="w-8 h-8 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
                      <p className="text-slate-500 font-medium text-sm">
                        Synchronizing Admin Matrix...
                      </p>
                    </div>
                  </td>
                </tr>
              ) : admins.length > 0 ? (
                admins.map((user) => (
                  <tr
                    key={user.AdminID}
                    className="hover:bg-slate-50/50 dark:hover:bg-slate-800 transition-colors group"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-[#1b254b] text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-white/10 flex items-center justify-center shrink-0">
                          <span className="font-bold text-sm tracking-widest text-slate-700 dark:text-slate-300">
                            {user.Username.substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-800 dark:text-slate-200">
                            {user.Username}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                            ID: {user.AdminID}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center border border-blue-100 dark:border-blue-800 shrink-0">
                          <Shield size={16} />
                        </div>
                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-[#1b254b] px-2.5 py-1 rounded-md border border-slate-200 dark:border-white/10">
                          {roleMap.get(user.RoleID) ?? "Admin"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer group/mail">
                          <Mail
                            size={14}
                            className="text-slate-400 dark:text-slate-500 group-hover/mail:text-blue-500 dark:group-hover/mail:text-blue-400"
                          />
                          <span className="text-sm font-medium">
                            {user.Email}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {user.IsActive ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 text-xs font-semibold border border-emerald-100 dark:border-emerald-800">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-100 dark:bg-[#1b254b] text-slate-600 dark:text-slate-400 text-xs font-semibold border border-slate-200 dark:border-white/10">
                          <div className="w-1.5 h-1.5 rounded-full bg-slate-400 dark:bg-slate-500" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2 transition-opacity">
                        <button
                          onClick={() => handleViewDetails(user)}
                          className="p-2 text-slate-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors border border-transparent hover:border-blue-100 dark:hover:border-blue-800"
                          title="View Profile"
                        >
                          <Eye size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <div className="w-12 h-12 bg-slate-50 dark:bg-[#1b254b] text-slate-400 dark:text-slate-500 rounded-2xl flex items-center justify-center border border-slate-100 dark:border-white/10">
                        <Users size={24} />
                      </div>
                      <p className="text-slate-500 dark:text-slate-400 font-medium text-sm">
                        No administrators found in current scan.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Detail Modal */}
      {isModalOpen && selectedUser && (
        <Modal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Administrator Profile"
          maxWidth="max-w-2xl"
          footer={
            <div className="flex justify-end items-center w-full gap-4">
              <Button
                onClick={() => setIsModalOpen(false)}
                className="bg-[#111c44] text-white hover:bg-black dark:hover:bg-[#1b254b] px-10 rounded-2xl font-bold"
              >
                Close
              </Button>
            </div>
          }
        >
          <div className="space-y-8 py-4">
            {/* Profile Header */}
            <div className="relative overflow-hidden rounded-[32px] bg-linear-to-br from-indigo-50 dark:from-[#1b254b] via-white dark:via-[#1b254b] to-blue-50 dark:to-[#1b254b] p-8 border border-blue-50 dark:border-white/10 shadow-sm">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
                <div className="w-24 h-24 rounded-3xl bg-linear-to-tr from-blue-600 to-indigo-700 flex items-center justify-center text-white text-3xl font-black shadow-xl shadow-blue-500/20">
                  {selectedUser.Username.substring(0, 2).toUpperCase()}
                </div>
                <div className="text-center md:text-left space-y-2">
                  <div className="flex flex-col md:flex-row items-center gap-3">
                    <h2 className="text-3xl font-black text-gray-900 dark:text-slate-100 tracking-tight">
                      {selectedUser.Username}
                    </h2>
                    <span className="px-3 py-1 bg-white dark:bg-[#1b254b] border border-blue-100 dark:border-white/10 rounded-xl text-[10px] font-black uppercase text-blue-600 dark:text-blue-400 tracking-widest">
                      {roleMap.get(selectedUser.RoleID) ?? "Admin"}
                    </span>
                  </div>
                  <p className="text-gray-500 dark:text-slate-400 font-medium">
                    Administrator Profile
                  </p>
                  <div className="flex items-center justify-center md:justify-start gap-6 pt-2">
                    <div className="flex items-center gap-2 text-xs font-bold text-gray-400 dark:text-slate-500">
                      <Calendar size={14} />
                      {" Joined "}
                      {formatDate(selectedUser.CreatedAt)}
                    </div>
                    <div
                      className={`flex items-center gap-2 text-xs font-bold ${selectedUser.IsActive && !selectedUser.IsLocked ? "text-emerald-500" : "text-rose-500"}`}
                    >
                      <div
                        className={`w-1.5 h-1.5 rounded-full ${selectedUser.IsActive && !selectedUser.IsLocked ? "bg-emerald-500" : "bg-rose-500"}`}
                      />
                      {selectedUser.IsLocked
                        ? "Locked"
                        : selectedUser.IsActive
                          ? "Active"
                          : "Inactive"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 rounded-3xl bg-white dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 shadow-sm space-y-4">
                <h4 className="text-[11px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-widest flex items-center gap-2">
                  <Mail size={14} />
                  Communication Details
                </h4>
                <div className="space-y-4">
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase">
                      Email Address
                    </p>
                    <p className="font-bold text-gray-800 dark:text-slate-200">
                      {selectedUser.Email}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase">
                      Admin ID
                    </p>
                    <p className="font-bold text-gray-800 dark:text-slate-200">
                      ADM-{selectedUser.AdminID}
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-3xl bg-white dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 shadow-sm space-y-4">
                <h4 className="text-[11px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-widest flex items-center gap-2">
                  <Clock size={14} />
                  Account Security
                </h4>
                <div className="space-y-4">
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase">
                      Last Session
                    </p>
                    <p className="font-bold text-gray-800 dark:text-slate-200">
                      {selectedUser.LastLoginAt
                        ? formatDateTime(selectedUser.LastLoginAt)
                        : "Never"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase">
                      Failed Logins
                    </p>
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-600 dark:text-amber-500">
                      {selectedUser.FailedLoginCount} attempts
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Role Management Modal */}
      {isCreateRoleModalOpen && (
        <Modal
          open={isCreateRoleModalOpen}
          onClose={() => setIsCreateRoleModalOpen(false)}
          title={
            roleView === "list"
              ? "Role Management"
              : roleView === "add"
                ? "Create New Role"
                : "Edit Role"
          }
          maxWidth="max-w-lg"
          footer={
            roleView === "list" ? null : (
              <div className="flex justify-end gap-3 w-full">
                <Button
                  variant="outline"
                  onClick={() => setRoleView("list")}
                  className="px-6 rounded-xl font-bold"
                  disabled={isCreatingRole}
                >
                  Back to List
                </Button>
                <Button
                  onClick={handleCreateRole}
                  className="bg-blue-600 text-white hover:bg-blue-700 px-8 rounded-xl font-bold"
                  disabled={isCreatingRole}
                >
                  {isCreatingRole
                    ? "Processing..."
                    : roleView === "add"
                      ? "Create Role"
                      : "Update Role"}
                </Button>
              </div>
            )
          }
        >
          {roleView === "list" ? (
            <div className="space-y-6 py-2">
              <div className="flex justify-between items-center">
                <h4 className="text-sm font-black text-gray-400 dark:text-slate-500 uppercase tracking-widest">
                  Available Roles
                </h4>
                <Button
                  onClick={() => {
                    setRoleName("");
                    setRoleDescription("");
                    setRoleView("add");
                  }}
                  className="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 border-none px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2"
                >
                  <ShieldCheck size={14} />
                  Add New Role
                </Button>
              </div>

              {isFetchingRoles ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : roles.length > 0 ? (
                <div className="grid grid-cols-1 gap-4 max-h-[50vh] overflow-y-auto p-4 bg-slate-50/80 dark:bg-[#1b254b]/30 rounded-[28px] border border-slate-100 dark:border-white/5 custom-scroll">
                  {roles.map((role) => (
                    <div
                      key={role.RoleID}
                      className="p-4 rounded-2xl bg-white dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 shadow-sm hover:border-blue-100 dark:hover:border-white/10 transition-all group flex justify-between items-center"
                    >
                      <div className="space-y-1 overflow-hidden flex-1 mr-4">
                        <p className="text-sm font-black text-gray-900 dark:text-slate-100 flex items-center gap-2">
                          <ShieldCheck size={14} className="text-blue-500" />
                          {role.RoleName}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-400 font-medium line-clamp-1 break-all">
                          {role.Description || "No description provided"}
                        </p>
                      </div>
                      <button
                        onClick={() => handleEditRole(role)}
                        className="p-2 text-gray-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-100/50 dark:hover:bg-blue-900/20 rounded-xl transition-all"
                      >
                        <Pencil size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 dark:bg-[#1b254b]/50 rounded-3xl border-2 border-dashed border-gray-100 dark:border-white/10">
                  <ShieldAlert
                    size={40}
                    className="mx-auto text-gray-300 dark:text-slate-600 mb-3"
                  />
                  <p className="text-gray-500 dark:text-slate-400 font-medium">
                    No roles found
                  </p>
                  <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase font-black mt-1">
                    Create your first security role
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6 py-2">
              <div className="p-4 rounded-3xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/30 space-y-1">
                <h4 className="text-xs font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest flex items-center gap-2">
                  <Activity size={14} />
                  {roleView === "add"
                    ? "Defining New Access Level"
                    : "Modifying Role Access"}
                </h4>
                <p className="text-[11px] text-blue-500 dark:text-blue-400 font-medium leading-relaxed">
                  {roleView === "add"
                    ? "Specify the role name and description to create a new permission level for platform administrators."
                    : "Update the role name and description. These changes will affect all users assigned to this role."}
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <p className="text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-[0.15em] ml-1">
                    Role Identifier
                  </p>
                  <Input
                    placeholder="e.g. Master Security Admin"
                    value={roleName}
                    onChange={(e: any) => setRoleName(e.target.value)}
                    className="rounded-2xl border-gray-100 dark:border-white/10 bg-white dark:bg-[#1b254b]/50 text-slate-900 dark:text-slate-100 focus:ring-blue-600/5 focus:border-blue-500"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <p className="text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-[0.15em] ml-1">
                    Scope & Responsibilities
                  </p>
                  <Textarea
                    placeholder="Briefly describe the permissions or administrative scope for this role..."
                    value={roleDescription}
                    onChange={(e: any) => setRoleDescription(e.target.value)}
                    className="rounded-2xl border-gray-100 dark:border-white/10 bg-white dark:bg-[#1b254b]/50 text-slate-900 dark:text-slate-100 focus:ring-blue-600/5 focus:border-blue-500 min-h-[120px]"
                    rows={4}
                    maxLength={200}
                  />
                  <div className="flex justify-end pr-1">
                    <p
                      className={`text-[10px] font-bold ${
                        roleDescription.length >= 200
                          ? "text-rose-500"
                          : "text-gray-400 dark:text-slate-500"
                      } uppercase tracking-wider`}
                    >
                      {roleDescription.length}/200 Characters
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* Create Admin Modal */}
      {isCreateAdminModalOpen && (
        <Modal
          open={isCreateAdminModalOpen}
          onClose={() => setIsCreateAdminModalOpen(false)}
          title="Create New Administrator"
          maxWidth="max-w-lg"
          footer={
            <div className="flex justify-end gap-3 w-full">
              <Button
                variant="outline"
                onClick={() => setIsCreateAdminModalOpen(false)}
                className="px-6 rounded-xl font-bold"
                disabled={isAdminCreating}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateAdmin}
                className="bg-blue-600 text-white hover:bg-blue-700 px-8 rounded-xl font-bold"
                disabled={isAdminCreating}
              >
                {isAdminCreating ? "Creating..." : "Create Admin"}
              </Button>
            </div>
          }
        >
          <div className="space-y-6 py-2">
            <div className="p-4 rounded-3xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/30 space-y-1">
              <h4 className="text-xs font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest flex items-center gap-2">
                <UserPlus size={14} />
                New Administrative Access
              </h4>
              <p className="text-[11px] text-blue-500 dark:text-blue-400 font-medium leading-relaxed">
                Complete the form below to grant administrative privileges.
                Ensure the email address is valid for verification.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-[0.15em] ml-1">
                  Username
                </p>
                <Input
                  placeholder="Admin Name"
                  value={adminForm.Username}
                  onChange={(e: any) =>
                    setAdminForm({ ...adminForm, Username: e.target.value })
                  }
                  className="rounded-2xl border-gray-100 dark:border-white/10 bg-white dark:bg-[#1b254b] text-slate-900 dark:text-slate-100 focus:ring-blue-600/5"
                  required
                />
              </div>
              <div className="space-y-2">
                <p className="text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-[0.15em] ml-1">
                  Email Address
                </p>
                <Input
                  type="email"
                  placeholder="admin@example.com"
                  value={adminForm.Email}
                  onChange={(e: any) =>
                    setAdminForm({ ...adminForm, Email: e.target.value })
                  }
                  className="rounded-2xl border-gray-100 dark:border-white/10 bg-white dark:bg-[#1b254b] text-slate-900 dark:text-slate-100 focus:ring-blue-600/5"
                  required
                />
              </div>
              <div className="space-y-2">
                <p className="text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-[0.15em] ml-1">
                  Password
                </p>
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={adminForm.PasswordHash}
                  onChange={(e: any) =>
                    setAdminForm({ ...adminForm, PasswordHash: e.target.value })
                  }
                  className="rounded-2xl border-gray-100 dark:border-white/10 bg-white dark:bg-[#1b254b] text-slate-900 dark:text-slate-100 focus:ring-blue-600/5"
                  required
                  suffix={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="hover:text-blue-600 transition-colors"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  }
                />
              </div>
              <div className="space-y-2">
                <p className="text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-[0.15em] ml-1">
                  Action PIN
                </p>
                <Input
                  type={showPin ? "text" : "password"}
                  placeholder="4-6 Digit PIN"
                  maxLength={6}
                  value={adminForm.ActionPinHash}
                  onChange={(e: any) =>
                    setAdminForm({
                      ...adminForm,
                      ActionPinHash: e.target.value,
                    })
                  }
                  className="rounded-2xl border-gray-100 dark:border-white/10 bg-white dark:bg-[#1b254b] text-slate-900 dark:text-slate-100 focus:ring-blue-600/5"
                  required
                  suffix={
                    <button
                      type="button"
                      onClick={() => setShowPin(!showPin)}
                      className="hover:text-blue-600 transition-colors"
                    >
                      {showPin ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  }
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <p className="text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-[0.15em] ml-1">
                  Assign Security Role
                </p>
                <div className="relative">
                  <select
                    value={adminForm.RoleID}
                    onChange={(e) =>
                      setAdminForm({
                        ...adminForm,
                        RoleID: Number(e.target.value),
                      })
                    }
                    className="w-full h-[52px] bg-white dark:bg-[#1b254b]/50 border border-gray-100 dark:border-white/10 rounded-2xl px-4 text-sm font-medium text-slate-900 dark:text-slate-100 focus:ring-4 focus:ring-blue-600/5 outline-none transition-all appearance-none cursor-pointer"
                    required
                  >
                    <option
                      value={0}
                      className="dark:bg-[#111c44] dark:text-slate-400"
                    >
                      Select a specific role...
                    </option>
                    {roles.map((role) => (
                      <option
                        key={role.RoleID}
                        value={role.RoleID}
                        className="dark:bg-[#111c44] dark:text-slate-200"
                      >
                        {role.RoleName}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                    <ChevronDown size={18} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdminUsers;
